/**
 * Extra shape packs for the wire's art desk. Most of these were drawn
 * for the 40th-anniversary supplemental and never removed. They are
 * bundled because the desk keeps asking "can we bring back the rosette"
 * and one day the answer will be yes.
 */

import {
  jitter,
  mulberry32,
  randomSeed,
  roughCircle,
  roughEllipse,
  roughLine,
  roughRect,
  scribbleFill,
  type RoughOpts,
} from "./core";

/** Laurel wreath, for awards the wire did not win. */
export function roughLaurel(cx: number, cy: number, rx: number, ry: number, opts?: RoughOpts): string[] {
  const { next, rough } = mulberry32(opts?.seed ?? randomSeed()) ? { next: mulberry32(opts?.seed ?? randomSeed()), rough: opts?.roughness ?? 1 } : { next: mulberry32(1), rough: 1 };
  const strokes: string[] = [];
  const leaves = 9;
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < leaves; i++) {
      const t = -0.4 + i * 0.22;
      const px = cx + Math.sin(t) * rx * side;
      const py = cy - Math.cos(t) * ry;
      const lx = px + side * (6 + jitter(next, 2));
      const ly = py + 3 + jitter(next, 2);
      strokes.push(
        `M${px.toFixed(1)} ${py.toFixed(1)} Q${((px + lx) / 2 + side * 3).toFixed(1)} ${((py + ly) / 2 - 3).toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)}`,
      );
    }
  }
  void rough;
  return strokes;
}

/** Newspaper rosette: concentric nervous circles on a stick. */
export function roughRosette(cx: number, cy: number, r: number, opts?: RoughOpts): string[] {
  const strokes: string[] = [];
  for (let i = 0; i < 4; i++) {
    strokes.push(...roughCircle(cx, cy, r * (1 - i * 0.18), { seed: (opts?.seed ?? randomSeed()) + i * 31, roughness: opts?.roughness }));
  }
  strokes.push(roughLine(cx, cy + r, cx, cy + r * 2.4, { seed: opts?.seed, roughness: opts?.roughness }));
  strokes.push(roughLine(cx - r * 0.5, cy + r * 1.9, cx + r * 0.5, cy + r * 1.9, { seed: (opts?.seed ?? 0) + 5, roughness: opts?.roughness }));
  return strokes;
}

/** Weather glyph: a cloud that has seen things. */
export function roughCloud(x: number, y: number, w: number, opts?: RoughOpts): string[] {
  const h = w * 0.55;
  const strokes: string[] = [
    ...roughEllipse(x + w * 0.32, y + h * 0.6, w * 0.26, h * 0.42, opts),
    ...roughEllipse(x + w * 0.62, y + h * 0.5, w * 0.22, h * 0.4, { seed: (opts?.seed ?? 0) + 8, roughness: opts?.roughness }),
    roughLine(x + w * 0.14, y + h * 0.86, x + w * 0.84, y + h * 0.86, { seed: (opts?.seed ?? 0) + 9, roughness: opts?.roughness }),
  ];
  return strokes;
}

/** Very approximate globe with latitude lines — the masthead sits on it. */
export function roughGlobe(cx: number, cy: number, r: number, opts?: RoughOpts): string[] {
  const strokes: string[] = [...roughCircle(cx, cy, r, opts)];
  for (const k of [-0.55, 0, 0.55]) {
    const ry = r * Math.sqrt(1 - k * k);
    strokes.push(...roughEllipse(cx, cy + k * r, ry, ry * 0.22, { seed: (opts?.seed ?? 0) + k * 100, roughness: opts?.roughness }));
  }
  strokes.push(...roughEllipse(cx, cy, r * 0.4, r, { seed: (opts?.seed ?? 0) + 41, roughness: opts?.roughness }));
  return strokes;
}

/** Duck. Long story. See: mascot litigation, Universe A-3. */
export function roughDuck(x: number, y: number, s: number, opts?: RoughOpts): string[] {
  const strokes: string[] = [];
  strokes.push(...roughEllipse(x + s * 0.62, y + s * 0.42, s * 0.3, s * 0.26, opts));
  strokes.push(...roughEllipse(x + s * 0.3, y + s * 0.18, s * 0.14, s * 0.12, { seed: (opts?.seed ?? 0) + 3, roughness: opts?.roughness }));
  strokes.push(roughLine(x + s * 0.18, y + s * 0.2, x + s * 0.02, y + s * 0.24, { seed: (opts?.seed ?? 0) + 4, roughness: opts?.roughness }));
  strokes.push(roughLine(x + s * 0.02, y + s * 0.24, x + s * 0.2, y + s * 0.3, { seed: (opts?.seed ?? 0) + 5, roughness: opts?.roughness }));
  strokes.push(roughLine(x + s * 0.34, y + s * 0.66, x + s * 0.34, y + s * 0.92, { seed: (opts?.seed ?? 0) + 6, roughness: opts?.roughness }));
  strokes.push(roughLine(x + s * 0.82, y + s * 0.66, x + s * 0.84, y + s * 0.92, { seed: (opts?.seed ?? 0) + 7, roughness: opts?.roughness }));
  return strokes;
}

/** Capsule / time-capsule glyph for the C-4 story. */
export function roughCapsule(x: number, y: number, w: number, h: number, opts?: RoughOpts): string[] {
  const strokes = roughRect(x, y, w, h, opts);
  strokes.push(roughLine(x + w * 0.2, y, x + w * 0.2, y + h, { seed: (opts?.seed ?? 0) + 2, roughness: opts?.roughness }));
  strokes.push(roughLine(x + w * 0.8, y, x + w * 0.8, y + h, { seed: (opts?.seed ?? 0) + 3, roughness: opts?.roughness }));
  return strokes;
}

/** Wax-seal-ish stamp: wobbly double ring + starburst. */
export function roughSeal(cx: number, cy: number, r: number, opts?: RoughOpts): string[] {
  const strokes: string[] = [
    ...roughCircle(cx, cy, r, opts),
    ...roughCircle(cx, cy, r * 0.74, { seed: (opts?.seed ?? 0) + 17, roughness: (opts?.roughness ?? 1) * 1.5 }),
  ];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    strokes.push(
      roughLine(cx + Math.cos(a) * r * 0.8, cy + Math.sin(a) * r * 0.8, cx + Math.cos(a) * r * 1.05, cy + Math.sin(a) * r * 1.05, {
        seed: (opts?.seed ?? 0) + i,
        roughness: opts?.roughness,
      }),
    );
  }
  return strokes;
}

/** Paper fold for the corner of "premium" cards. */
export function roughFold(x: number, y: number, s: number, opts?: RoughOpts): string[] {
  return [
    roughLine(x, y, x + s, y, { seed: opts?.seed, roughness: opts?.roughness }),
    roughLine(x + s, y, x + s, y + s, { seed: (opts?.seed ?? 0) + 1, roughness: opts?.roughness }),
    roughLine(x, y, x + s, y + s, { seed: (opts?.seed ?? 0) + 2, roughness: opts?.roughness }),
  ];
}

/** Denim cross-hatch for sold-out plaques. Kept for the archive. */
export function roughCrosshatch(x: number, y: number, w: number, h: number, opts?: RoughOpts): string[] {
  const strokes: string[] = [...scribbleFill(x, y, w, h, opts)];
  const { next } = mulberry32((opts?.seed ?? randomSeed()) + 90);
  for (let i = 0; i < 5; i++) {
    const xx = x + (w / 5) * i + jitter(next, 2);
    strokes.push(roughLine(xx, y, xx + w * 0.18, y + h, { seed: (opts?.seed ?? 0) + 100 + i, roughness: opts?.roughness }));
  }
  return strokes;
}
